import type { ColumnOptions, DeepPartial } from 'typeorm';
import { Column as TypeOrmColumn } from 'typeorm';
import type { ColumnCommonOptions } from 'typeorm/decorator/options/ColumnCommonOptions';
import type { ColumnEmbeddedOptions } from 'typeorm/decorator/options/ColumnEmbeddedOptions';
import type { ColumnEnumOptions } from 'typeorm/decorator/options/ColumnEnumOptions';
import type { ColumnHstoreOptions } from 'typeorm/decorator/options/ColumnHstoreOptions';
import type { ColumnNumericOptions } from 'typeorm/decorator/options/ColumnNumericOptions';
import type { ColumnWithLengthOptions } from 'typeorm/decorator/options/ColumnWithLengthOptions';
import type { ColumnWithWidthOptions } from 'typeorm/decorator/options/ColumnWithWidthOptions';
import type { SpatialColumnOptions } from 'typeorm/decorator/options/SpatialColumnOptions';
import type {
	SimpleColumnType,
	SpatialColumnType,
	WithLengthColumnType,
	WithPrecisionColumnType,
	WithWidthColumnType,
} from 'typeorm/driver/types/ColumnTypes';
import type { ObjectFieldSelectOptionsEntity } from '../models';
import type { PromisifyRelations } from '../types';

export interface CustomColumnMetadata {
	label?: string;
	selectOptions?: Array<
		DeepPartial<ObjectFieldSelectOptionsEntity> &
			DeepPartial<PromisifyRelations<ObjectFieldSelectOptionsEntity>>
	>;
}

const customColumnMetadataStorage = new Map<
	string,
	CustomColumnMetadata & { propertyName: string; target: Function }
>();

export function Column(
	options?: ColumnOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: SimpleColumnType,
	options?: ColumnCommonOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: SpatialColumnType,
	options?: ColumnCommonOptions & SpatialColumnOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: WithLengthColumnType,
	options?: ColumnCommonOptions & ColumnWithLengthOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: WithWidthColumnType,
	options?: ColumnCommonOptions & ColumnWithWidthOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: WithPrecisionColumnType,
	options?: ColumnCommonOptions & ColumnNumericOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: 'enum' | 'simple-enum' | 'set',
	options?: ColumnCommonOptions & ColumnEnumOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: 'hstore',
	options?: ColumnCommonOptions & ColumnHstoreOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	type: (type?: unknown) => Function,
	options?: ColumnEmbeddedOptions,
	customOptions?: CustomColumnMetadata,
): PropertyDecorator;
export function Column(
	...params: Array<
		| ColumnOptions
		| SimpleColumnType
		| SpatialColumnType
		| WithLengthColumnType
		| WithWidthColumnType
		| WithPrecisionColumnType
		| 'enum'
		| 'simple-enum'
		| 'set'
		| 'hstore'
		| ((type?: unknown) => Function)
		| CustomColumnMetadata
		| ColumnCommonOptions
		| (ColumnCommonOptions & SpatialColumnOptions)
		| (ColumnCommonOptions & ColumnWithLengthOptions)
		| (ColumnCommonOptions & ColumnWithWidthOptions)
		| (ColumnCommonOptions & ColumnNumericOptions)
		| (ColumnCommonOptions & ColumnEnumOptions)
		| (ColumnCommonOptions & ColumnHstoreOptions)
		| ColumnEmbeddedOptions
		| undefined
	>
): PropertyDecorator {
	let customOptions: CustomColumnMetadata | undefined;

	if (
		params.length === 3 ||
		(params.length === 2 && typeof params[0] === 'object')
	) {
		customOptions = params.pop() as CustomColumnMetadata;
	}

	return (target: object, propertyName: string | symbol) => {
		const key = `${target.constructor.name}.${propertyName as string}`;

		customColumnMetadataStorage.set(key, {
			propertyName: propertyName as string,
			target: target.constructor,
			...customOptions,
		});

		TypeOrmColumn(...(params as [ColumnOptions]))(target, propertyName);
	};
}

// Helper to retrieve custom metadata
export function getCustomColumnMetadata(
	entityClass: Function,
	propertyName: string,
): CustomColumnMetadata | undefined {
	const key = `${entityClass.name}.${propertyName}`;

	return customColumnMetadataStorage.get(key);
}
